import moment from 'moment';
import { MiddlewareAPI, Dispatch, Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { FormData } from '../api/fetchDoctorGenieByOID';
import {
    CalculatorsConfig,
    ExtraFlags,
    GeniePtColumns,
    GenieSettings,
    GMedicalHistoryConfig,
    StaffRestriction,
    VitalsConfig,
} from '../api/pushGenie';
import { PadDForm, PadFDFormElement, PrintConfig, Translate } from '../api/pushVisit';
import { SOURCE_MEDICAL_RECORDS } from '../components/common/MediaViewer';
import { SITE_OF_APPLICATION } from '../components/prescriptionPad/rightPanel/workspace/mxProperties';
import { Inventory } from '../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import PROPERTIES from '../components/prescriptionPad/rightPanel/workspace/properties';
import {
    LOCAL_ID_PREFIX,
    MAX_G_DIAGNOSIS,
    MAX_G_SYMPTOMS,
    MAX_G_MEDICATIONS,
    MAX_G_ADVICES,
    MAX_G_PRESCRIPTION_TEMPLATES,
    MAX_G_VITALS,
    MAX_G_EXAMINATIONS,
    MAX_G_FOLLOWUP,
    MAX_G_SERVICES,
    MAX_G_DYFORM,
    MAX_GENIE,
    MAX_G_LAB_VITALS,
    MAX_G_LT_TEMPLATES,
    MAX_G_MED_TEMPLATES,
    MAX_G_SYM_TEMPLATES,
    MAX_G_DIA_TEMPLATES,
    MAX_G_REFER,
    GenieVersion,
    MAX_INVENTORY,
    MAX_G_LAB_VITALS_TEMPLATES,
    MAX_G_LAB_TESTS,
    MAX_G_EXAMINATIONS_TEMPLATES,
    PRINT_CONFIGS_KEYS_TO_LABEL,
    GenieLastUpdatedAt,
    MAX_SNACKING_QUESTION,
    MAX_G_DYFORM_ELEMENTS,
    MAX_G_DENTAL_EXAMINATIONS,
    MAX_G_DENTAL_PROCEDURES,
    MAX_G_DENTAL_PAST_PROCEDURES,
    MAX_G_MEDICAL_RECORDS_TAGS,
    MAX_G_TREATMENT_SERVICES,
    MAX_G_TREATMENT_PACKAGES,
    MAX_G_PROCEDURES,
    MAX_G_PROCEDURES_TEMPLATES,
} from '../constants';
import { captureError, captureErrorString } from '../pages/captureError';
import { GO_TO_SETUP_APP } from '../routes/paths';
import { track } from '../tracking';
import { Actions } from './actions';
import { UPDATE_PT_COLUMNS } from './actions/genie';
import { UPDATE_GENIE_PROFILE } from './actions/login';
import {
    CREATE_PRESCRIPTION_TEMPLATE,
    DELETE_ADVICE_PERMANENTLY,
    EDIT_ADVICE,
    DeleteDyFormAction,
    DELETE_DYFORM,
    DELETE_PRESCRIPTION_TEMPLATE,
    DELETE_VITALS,
    FINISH_PRESCRIPTION,
    updatePrintConfig,
    UPDATE_TRANSLATE,
    UPDATE_VITALS,
    UPDATE_VITALS_V0,
    UpsertDyFormAction,
    UPSERT_DYFORM,
    UPSERT_PRESCRIPTION,
    applyPrescriptionTemplate,
    UPSERT_PRESCRIPTION_V2,
} from './actions/prescriptionPad';
import { UPDATE_STAR_VITALS, loadPadDyForm, upsertGenieSetting } from './actions/session';
import { pushGenieToBE } from './appReduxMiddleware';
import {
    GAdvice,
    GDyForm,
    GenieItem,
    GExamination,
    GFollowUp,
    GFollowUpData,
    GLabTest,
    GLabVital,
    GLTTemplate,
    GMedTemplate,
    GSymTemplate,
    GDiaTemplate,
    GMedFrequency,
    GMedication,
    GMedSOA,
    GMedTiming,
    GPTemplate,
    GRefer,
    GReferData,
    GService,
    GSymptomsDiagnosis,
    GVitals,
    GInventoryItem,
    GLabVitalTemplate,
    GExaminationsTemplate,
    SectionNameConfig,
    GInjectionTemplate,
    GInjection,
    GTreatmentService,
    GTreatmentPackage,
    GProceduresTemplate,
} from './db';
import { DB_TABLES } from './dbTables';
import {
    updateG,
    getGCount,
    purgeG,
    getGArray,
    upsertPrintConfig,
    updateDForms,
    updatePtColumns,
    updateItemInMisc,
    MISC_KEYS,
    deleteG,
    getItemFromMisc,
    deleteGenieTable,
    clearTable,
    getGenieItemById,
    getGById,
} from './idbHandler';
import { GlobalState } from './reducers';
import { getPrintConfigs } from './selectors/prescriptionPad';
import { getPxElementConfig } from './selectors/session';
import {
    AdvicesEntity,
    DENTAL_ENTITY_TYPE,
    DentalExamination,
    DentalProcedure,
    DiagnosisEntityOrSymptomsEntity,
    ExaminationEntity,
    Followup,
    LabTestsEntityOrProceduresEntity,
    LabVitalEntity,
    MedDoseInfo,
    GMedicalRecordsTags,
    MedicationsEntity,
    Tool,
    InjectionsEntity,
    DoctorCalendarConfigs,
    TDoctorEvitalsConfigs,
    Procedure,
} from './types/prescription';
import { cloneAndUpsert, cloneAndUpsertArray, generateElementId, getUnit } from './utils';
import omit from 'lodash/omit';
import { getMyDbId } from '../api/mactiveid';
import {
    BizSettings,
    Dicom,
    Ipd,
    PtDir,
    ReferralConfig,
    Role,
    TreatmentServicesAndTreatmentPackages,
} from '../api/fetchMyGenie';
import { getActiveDoctorId, getMActiveId } from './localstorage';
import { initAccessControl } from './AccessControl';
import { v4 } from 'uuid';

interface Item {
    id?: string;
    _id?: string;
}

interface GItem extends GenieItem {
    id: string;
}

export const updateGenie = async <ItemType extends Item>(
    id: string,
    arg: {
        key: DB_TABLES;
        value: ItemType[];
        incrementFCount?: boolean;
    },
    cleanup: boolean,
    ignoreMetadata?: boolean,
): Promise<void> => {
    const currentTimestamp = new Date().getTime();
    if (arg.incrementFCount) {
        const allItems: GItem[] = await getGArray<GItem>(id, arg.key);
        const fMap: { [id: string]: number } = {};
        allItems.forEach((i) => {
            fMap[i.id] = i.meta?.fCount || 0;
        });
        const gItems = arg.value.map((i) => ({
            ...i,
            meta: {
                updatedAt: currentTimestamp,
                isLocal: i.id?.startsWith(LOCAL_ID_PREFIX) ? 1 : 0,
                fCount: (fMap[i.id || ''] || 0) + 1,
                do_not_purge: (i as GenieItem).meta?.do_not_purge,
            },
        }));

        if (cleanup) {
            await clearTable(id, arg.key);
        }

        await updateG(id, arg.key, gItems);
        purge(arg.key);
    } else {
        // pulled from BE

        if (cleanup) {
            await clearTable(id, arg.key);
        }

        const valuesWithMeta = arg.value.map((i) => ({
            ...i,
            meta: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                updatedAt: i.meta?.updatedAt || currentTimestamp,
                isLocal: i.id?.startsWith(LOCAL_ID_PREFIX) ? 1 : 0,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                fCount: !i.meta?.fCount || isNaN(i.meta?.fCount) ? 0 : i.meta.fCount,
                do_not_purge: (i as GenieItem).meta?.do_not_purge,
            },
        }));

        const values = ignoreMetadata ? arg.value : valuesWithMeta;

        await updateG(id, arg.key, values as GenieItem[]);
    }
};

type A = {
    symptoms?: (DiagnosisEntityOrSymptomsEntity | GSymptomsDiagnosis)[];
    diagnosis?: (DiagnosisEntityOrSymptomsEntity | GSymptomsDiagnosis)[];
    medications?: (MedicationsEntity | GMedication)[];
    injections?: (InjectionsEntity | GInjection)[];
    advices?: (AdvicesEntity | GAdvice)[];
    labTests?: (LabTestsEntityOrProceduresEntity | GLabTest)[];
    vitals?: GVitals[];
    templates?: GPTemplate[];
    ltTemplates?: GLTTemplate[];
    medTemplates?: GMedTemplate[];
    injectionTemplates?: GInjectionTemplate[];
    symTemplates?: GSymTemplate[];
    diaTemplates?: GDiaTemplate[];
    labVitalsTemplates?: GLabVitalTemplate[];
    examinationsTemplates?: GExaminationsTemplate[];
    proceduresTemplates?: GProceduresTemplate[];
    examinations?: (GExamination | ExaminationEntity)[];
    refer?: GRefer[];
    printConfig?: PrintConfig;
    ptColumns?: GeniePtColumns;
    followUp?: GFollowUp[] | GFollowUpData[];
    forms?: FormData;
    settings?: GenieSettings;
    medicalHistoryConfig?: GMedicalHistoryConfig;
    vitalsConfig?: VitalsConfig;
    calculatorConfig?: CalculatorsConfig;
    sectionNameConfig?: SectionNameConfig;
    services?: GService[];
    translate?: Translate;
    padDForms?: (GDyForm | PadDForm)[];
    medFrequency?: GMedFrequency[];
    medSOA?: GMedSOA[];
    medTiming?: GMedTiming[];
    labVitals?: GLabVital[];
    version?: number;
    inventory?: Inventory;
    inventoryV2?: Inventory;
    staffRestriction?: StaffRestriction;
    keyVitals?: string[];
    extraFlags?: ExtraFlags;
    dentalExaminations?: DentalExamination[];
    dentalProcedures?: DentalProcedure[];
    dentalPastProcedures?: DentalExamination[];
    procedures?: Procedure[];
    medicalRecordsTags?: GMedicalRecordsTags[];
    treatmentServices?: GTreatmentService[];
    treatmentPackages?: GTreatmentPackage[];
    treatmentServicesAndTreatmentPackages?: TreatmentServicesAndTreatmentPackages;
    calendarConfigs?: DoctorCalendarConfigs;
    evitalsConfig?: TDoctorEvitalsConfigs;
    roles?: Role[];
    dicom?: Dicom;
    ipd?: Ipd;
    ptDir?: PtDir;
    bizSettings?: BizSettings;
    referral?: ReferralConfig;
    groupAccess?: {
        enabled?: boolean;
    };
};
const updateGenieProfile = async (
    id: string,
    {
        symptoms,
        diagnosis,
        medications,
        injections,
        advices,
        labTests,
        vitals,
        templates,
        ltTemplates,
        medTemplates,
        injectionTemplates,
        symTemplates,
        diaTemplates,
        labVitalsTemplates,
        examinationsTemplates,
        proceduresTemplates,
        examinations,
        refer,
        printConfig,
        forms,
        ptColumns,
        settings,
        medicalHistoryConfig,
        vitalsConfig,
        calculatorConfig,
        sectionNameConfig,
        inventory,
        inventoryV2,
        followUp,
        services,
        translate,
        padDForms,
        medFrequency,
        medSOA,
        medTiming,
        labVitals,
        version,
        staffRestriction,
        keyVitals,
        extraFlags,
        dentalExaminations,
        dentalProcedures,
        dentalPastProcedures,
        procedures,
        medicalRecordsTags,
        treatmentServices,
        treatmentPackages,
        treatmentServicesAndTreatmentPackages,
        calendarConfigs,
        evitalsConfig,
        roles,
        dicom,
        ipd,
        ptDir,
        bizSettings,
        referral,
        groupAccess,
    }: A,
    incrementFCount?: boolean,
    trackingId?: string,
) => {
    const promises = [];
    if (version !== undefined && version !== null)
        promises.push(
            updateItemInMisc<GenieVersion>(id, MISC_KEYS.genieVersion, {
                version,
            }),
        );

    if (printConfig) {
        promises.push(upsertPrintConfig(id, printConfig));
    }

    if (ptColumns) {
        promises.push(updatePtColumns(id, ptColumns));
    }

    // if (inventory) {
    //     promises.push(
    //         updateGenie<GInventoryItem>(
    //             id,
    //             {
    //                 key: DB_TABLES['inventory'],
    //                 value: Object.entries(inventory).map((item) => ({
    //                     id: item[0],
    //                     ...item[1],
    //                 })),
    //                 incrementFCount,
    //             },
    //             true,
    //         ),
    //     );
    // }

    // if (inventoryV2) {
    //     promises.push(
    //         updateGenie<GInventoryItem>(
    //             id,
    //             {
    //                 key: DB_TABLES['inventoryV2'],
    //                 value: Object.entries(inventoryV2).map((item) => ({
    //                     id: item[0],
    //                     ...item[1],
    //                 })),
    //                 incrementFCount,
    //             },
    //             true,
    //         ),
    //     );
    // }

    if (labVitals) {
        promises.push(
            await updateGenie<LabVitalEntity>(
                id,
                {
                    key: DB_TABLES['labVitals'],
                    value: labVitals.map((t) => ({
                        id: t.id,
                        name: t.name,
                        unit_dislay_name: t.unit_dislay_name,
                        all_units: t.all_units,
                        unit: t.unit,
                        is_panel: t.is_panel,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        meta: t.meta,
                    })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );
    }

    symptoms &&
        promises.push(
            updateGenie<DiagnosisEntityOrSymptomsEntity>(
                id,
                {
                    key: DB_TABLES['symptoms'],
                    value: symptoms.map((s) => ({
                        id: s.id,
                        name: s.name,
                        icd10_name: s?.icd10_name,
                        icd10_code: s?.icd10_code,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        meta: s.meta,
                    })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    diagnosis &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['diagnosis'],
                    value: diagnosis.map((s) => ({
                        id: s.id,
                        name: s.name,
                        icd10_name: s?.icd10_name,
                        icd10_code: s?.icd10_code,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        meta: s.meta,
                    })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );
    injections &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['injections'],
                    value: injections,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    medications &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['medications'],
                    value: medications.map(
                        (d): MedicationsEntity => ({
                            id: d.id,
                            name: d.name,
                            form: d.form,
                            dosage_form: d.dosage_form,
                            df_id: d.df_id,
                            dose: d.dose,
                            generic_id: d.generic_id,
                            generic_name: d.generic_name,
                            frequency: d.frequency,
                            area: d.area,
                            timing: d.timing,
                            duration: d.duration,
                            instruction: d.instruction,
                            product_type: d.product_type || d.form,
                            tapering_dose: d.tapering_dose,
                            actual_name: d.actual_name,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            meta: d.meta,
                            soa: d.soa,
                            is_otc: d.is_otc,
                            manufacturer_name: d.manufacturer_name,
                            manufacturer_id: d.manufacturer_id,
                            med_cat: d.med_cat,
                            hxng_mapped: d.hxng_mapped,
                            hxng_status: d.hxng_status,
                            meta_info: d.meta_info,
                        }),
                    ),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    advices &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['advices'],
                    value: advices.map((a) => ({
                        id: a.id,
                        text: a.text,
                        parsedText: a.parsedText,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        meta: a.meta,
                    })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    labTests &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['labTests'],
                    value: labTests.map((l) => ({
                        id: l.id,
                        name: l.name,
                        remark: l.remark,
                        book: l.book,
                        type: l.type,
                        content: l.content,
                        vendor_names: l.vendor_names,
                        vendor_ids: l.vendor_ids,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        meta: l.meta,
                        hxng_only: l.hxng_only,
                    })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    vitals &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['vitals'],
                    value: vitals.map((v) => ({
                        id: v.id,
                        name: v.name,
                        unit: v.unit,
                        meta: v.meta,
                    })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    templates &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['prescriptionTemplates'],
                    value: templates,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    ltTemplates &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['gLTTemplates'],
                    value: ltTemplates,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    medTemplates &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['gMedTemplates'],
                    value: medTemplates,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );
    injectionTemplates &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['gInjectionTemplates'],
                    value: injectionTemplates,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    symTemplates &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['gSymTemplates'],
                    value: symTemplates,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    diaTemplates &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['gDiaTemplates'],
                    value: diaTemplates,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    labVitalsTemplates &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['gLabVitalsTemplates'],
                    value: labVitalsTemplates,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    examinationsTemplates &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['gExaminationsTemplates'],
                    value: examinationsTemplates,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    proceduresTemplates &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['gProceduresTemplates'],
                    value: proceduresTemplates,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    examinations &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['examinations'],
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    value: examinations.map((e) => ({ id: e.id, name: e.name, meta: e.meta })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );
    refer &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['refer'],
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    value: refer,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    forms && promises.push(updateDForms(forms));

    followUp &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['followUp'],
                    value: followUp,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    settings && promises.push(updateItemInMisc(id, MISC_KEYS.settings, settings));
    groupAccess && promises.push(updateItemInMisc(id, MISC_KEYS.groupAccess, groupAccess));
    medicalHistoryConfig &&
        promises.push(updateItemInMisc(id, MISC_KEYS.medicalHistoryConfig, medicalHistoryConfig));
    vitalsConfig && promises.push(updateItemInMisc(id, MISC_KEYS.vitalsConfig, vitalsConfig));
    keyVitals && promises.push(updateItemInMisc(id, MISC_KEYS.keyVitals, keyVitals));
    extraFlags && promises.push(updateItemInMisc(id, MISC_KEYS.extraFlags, extraFlags));
    calculatorConfig &&
        promises.push(updateItemInMisc(id, MISC_KEYS.calculatorConfig, calculatorConfig));
    sectionNameConfig &&
        promises.push(updateItemInMisc(id, MISC_KEYS.sectionNameConfig, sectionNameConfig));
    staffRestriction &&
        promises.push(updateItemInMisc(id, MISC_KEYS.staffRestriction, staffRestriction));
    translate && promises.push(updateItemInMisc(id, MISC_KEYS.translate, translate));
    calendarConfigs &&
        promises.push(updateItemInMisc(id, MISC_KEYS.calendarConfigs, calendarConfigs));
    evitalsConfig && promises.push(updateItemInMisc(id, MISC_KEYS.evitalsConfig, evitalsConfig));

    services &&
        promises.push(
            updateGenie(id, { key: DB_TABLES['services'], value: services }, !incrementFCount),
        );

    padDForms && promises.push(updatePadDyForms(id, padDForms, incrementFCount || false));

    medFrequency &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['medFrequency'],
                    value: medFrequency,
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );

    medSOA &&
        promises.push(
            updateGenie(
                id,
                { key: DB_TABLES['medSOA'], value: medSOA, incrementFCount },
                !incrementFCount,
            ),
        );

    medTiming &&
        promises.push(
            updateGenie(
                id,
                { key: DB_TABLES['medTiming'], value: medTiming, incrementFCount },
                !incrementFCount,
            ),
        );
    dentalExaminations &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['dentalExaminations'],

                    value: dentalExaminations.map((e) => ({
                        id: e.id,
                        name: e.name,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        meta: e.meta,
                    })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );
    medicalRecordsTags &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['medicalRecordsTags'],

                    value: medicalRecordsTags.map((e) => ({
                        id: e.id,
                        name: e.name,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        meta: e.meta,
                    })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );
    dentalProcedures &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['dentalProcedures'],
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    value: dentalProcedures.map((e) => ({ id: e.id, name: e.name, meta: e.meta })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );
    dentalPastProcedures &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['dentalPastProcedures'],
                    value: dentalPastProcedures
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        .map((e) => ({ id: e.id, name: e.name, meta: e.meta })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );
    procedures &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['procedures'],
                    value: procedures
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        .map((e) => ({ id: e.id, name: e.name, meta: e.meta })),
                    incrementFCount,
                },
                !incrementFCount,
            ),
        );
    treatmentServices &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['treatmentServices'],
                    value: treatmentServices,
                    incrementFCount: false,
                },
                false,
                true,
            ),
        );
    treatmentPackages &&
        promises.push(
            updateGenie(
                id,
                {
                    key: DB_TABLES['treatmentPackages'],
                    value: treatmentPackages,
                    incrementFCount: false,
                },
                false,
                true,
            ),
        );
    treatmentServicesAndTreatmentPackages &&
        promises.push(
            updateItemInMisc(
                id,
                MISC_KEYS.treatmentServicesAndTreatmentPackages,
                treatmentServicesAndTreatmentPackages,
            ),
        );
    dicom && promises.push(updateItemInMisc(id, MISC_KEYS.dicom, dicom));
    ipd && promises.push(updateItemInMisc(id, MISC_KEYS.ipd, ipd));
    referral && promises.push(updateItemInMisc(id, MISC_KEYS.referralConfig, referral));
    roles && promises.push(updateG(id, DB_TABLES.roles, roles as GenieItem[]));
    ptDir && promises.push(updateItemInMisc(id, MISC_KEYS.ptDir, ptDir));
    bizSettings && promises.push(updateItemInMisc(id, MISC_KEYS.bizSettings, bizSettings));
    if (roles) {
        const allRoles = roles.map((i) => i.id);
        track('ACCESS_CONTROL_ROLES', { trackingId, roles: allRoles.join(', ') });
    }

    await Promise.all(promises);
};

const updatePadDyForms = async (
    id: string,
    dyForm: (PadDForm | GDyForm)[],
    incrementFCount: boolean,
) => {
    const currentSections = await getGArray<GDyForm>(id, DB_TABLES.dyForm);
    if (!incrementFCount) {
        const combinedData: GDyForm[] = [];
        for (let index = 0; index < dyForm.length; index++) {
            const item = dyForm[index];
            const alreadyPresent = currentSections.find((t) => t.id === item.id);
            if (!alreadyPresent) {
                combinedData.push(item);
                continue;
            }
            if (!alreadyPresent.elements) {
                alreadyPresent.elements = item.elements;
                combinedData.push(alreadyPresent);
                continue;
            }
            alreadyPresent.elements = cloneAndUpsertArray(
                item.elements || [],
                alreadyPresent?.elements,
            );
            combinedData.push(alreadyPresent);
        }

        await clearTable(id, DB_TABLES.dyForm);
        // from BE
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await updateG(id, DB_TABLES.dyForm, combinedData);
        return;
    }
    for (let index = 0; index < dyForm.length; index++) {
        const item = dyForm[index];
        if (!item.elements || item.elements.length === 0) {
        } else {
            const alreadyPresent = currentSections.find((t) => t.id === item.id);
            const newItems: PadFDFormElement[] = item.elements.map((t) => ({
                ...t,
                meta: {
                    fCount: (t.meta?.fCount || 0) + 1,
                    updatedAt: new Date().getTime(),
                },
            }));

            if (alreadyPresent && alreadyPresent.elements) {
                alreadyPresent.elements = purgeDyFormElements(
                    cloneAndUpsertArray(newItems, alreadyPresent?.elements),
                );
                if (alreadyPresent['meta']) {
                    alreadyPresent['meta'] = {
                        ...alreadyPresent['meta'],
                        fCount: (alreadyPresent['meta']?.fCount || 0) + 1,
                        updatedAt: new Date().getTime(),
                    };
                }
            } else if (alreadyPresent) {
                alreadyPresent.elements = purgeDyFormElements(newItems);
                if (alreadyPresent['meta']) {
                    alreadyPresent['meta'] = {
                        ...alreadyPresent['meta'],
                        fCount: (alreadyPresent['meta']?.fCount || 0) + 1,
                        updatedAt: new Date().getTime(),
                    };
                }
            } else {
                currentSections.push({
                    ...item,
                    elements: purgeDyFormElements(newItems),
                    meta: { fCount: 1, isLocal: 1, updatedAt: new Date().getTime() },
                });
            }
        }
    }

    await updateG(id, DB_TABLES.dyForm, currentSections);
};

const handleAction = async (
    action: Actions,
    state: GlobalState,
    dispatch: ThunkDispatch<GlobalState, null, Actions>,
): Promise<void> => {
    switch (action.type) {
        case UPSERT_DYFORM:
            handleUpsertDyForm(action, state, dispatch);
            break;
        case DELETE_DYFORM:
            handleDeleteDyForm(action, state, dispatch);
            break;
        case UPDATE_TRANSLATE:
            await updateItemInMisc(getMyDbId() || '', MISC_KEYS.translate, action.translate);
            await pushGenieToBE(getMyDbId() || '', ['translate']);
            break;
        case UPDATE_PT_COLUMNS:
            const id = getActiveDoctorId() || getMActiveId() || '';
            await updatePtColumns(id, action.columns);
            await pushGenieToBE(id, ['ptColumns']);
            return;
        case UPDATE_GENIE_PROFILE:
            const { genieProfiles, onSuccess } = action;
            const trackingId = v4();
            const promises = [];
            for (let index = 0; index < genieProfiles.length; index++) {
                const genieProfile = genieProfiles[index];
                const { id } = genieProfiles[index];
                const currentVersion = await getItemFromMisc<GenieVersion>(
                    id,
                    MISC_KEYS.genieVersion,
                );
                if (currentVersion.version && currentVersion.version !== genieProfile.version) {
                    track('VERSION_UPDATE', {
                        on: 'UPDATE_GENIE_PROFILE',
                        currentVersion: currentVersion.version?.toString(),
                        genieVersion: genieProfile.version?.toString(),
                    });
                    // delete all
                    await deleteGenieTable(id);
                    await updateItemInMisc(id, MISC_KEYS.genieVersion, { version: undefined });
                    await updateItemInMisc<GenieLastUpdatedAt>(id, MISC_KEYS.genieLastUpdated, {
                        isoString: undefined,
                    });
                    alert("You've got a new version of the application. Please refresh the page.");
                    window.location.replace(GO_TO_SETUP_APP);
                    return;
                }

                try {
                    promises.push(updateGenieProfile(id, genieProfile, false, trackingId));
                    promises.push(
                        updateItemInMisc(id, MISC_KEYS.genieLastUpdated, {
                            isoString: new Date().toISOString(),
                        }),
                    );
                } catch (e) {
                    console.error('Error upserting genie data', e);
                }
            }

            await Promise.all(promises);

            onSuccess && onSuccess();
            // TODO needs to be moved to ACL
            initAccessControl(trackingId);
            break;

        case CREATE_PRESCRIPTION_TEMPLATE:
            let source: Tool;

            if (action.data) {
                source = action.data;
            } else if (state.prescriptionPad.prescription) {
                source = state.prescriptionPad.prescription;
            } else {
                break;
            }

            switch (action.entity) {
                case 'PX':
                    const template: GPTemplate = {
                        id: action.id || `${LOCAL_ID_PREFIX}${new Date().getTime()}`,
                        name: action.name,

                        symptoms: source.symptoms?.map((s) => ({ id: s.id, name: s.name })),
                        diagnosis: source.diagnosis?.map((d) => ({ id: d.id, name: d.name })),
                        medications: source.medications,
                        injections: source.injections,
                        labTests: source.labTests?.map((LabTest) =>
                            omit(LabTest, ['test_on', 'repeat_on']),
                        ),
                        labVitals: source.labVitals?.map((t) => ({
                            id: t.id,
                            name: t.name,
                            unit_dislay_name: t.unit_dislay_name,
                            all_units: t.all_units,
                            unit: t.unit,
                            is_panel: t.is_panel,
                        })),
                        advices: source.advices,
                        followup: {
                            ...getTemplatizedFollowUp(source.followup),
                            notes: source.followup?.notes,
                        },
                        meta: {
                            updatedAt: new Date().getTime(),
                            isLocal: 1,
                            fCount: 0, // NA
                        },
                        dyform: source.dyform,
                        procedures: source.procedures,
                        careCanvas: source.careCanvas,
                    };

                    await updateGenie(
                        getMyDbId() || '',
                        {
                            key: DB_TABLES['prescriptionTemplates'],
                            value: [template],
                        },
                        false,
                    );

                    await pushGenieToBE(getMyDbId() || '', ['templates']);
                    break;

                case 'labTests':
                    const id = action.id || `${LOCAL_ID_PREFIX}${new Date().getTime()}`;
                    const elTemplate: GLTTemplate = {
                        id: id,
                        name: action.name,
                        [action.entity]: (source[action.entity] || []).map((l) => ({
                            id: l.id,
                            name: l.name,
                            remark: l.remark,
                        })),
                        isPackage: action.isPackage,
                    };
                    if (!action.isPackage) delete elTemplate.isPackage;

                    await updateGenie(
                        getMyDbId() || '',
                        {
                            key: DB_TABLES['gLTTemplates'],
                            value: [elTemplate],
                        },
                        false,
                    );

                    await pushGenieToBE(getMyDbId() || '', ['ltTemplates']);
                    action.isPackage && dispatch(applyPrescriptionTemplate('labTests', id));

                    break;

                case 'medications':
                    const medTemplate: GMedTemplate = {
                        id: action.id || `${LOCAL_ID_PREFIX}${new Date().getTime()}`,
                        name: action.name,

                        [action.entity]: source[action.entity] || [],
                    };

                    await updateGenie(
                        getMyDbId() || '',
                        {
                            key: DB_TABLES['gMedTemplates'],
                            value: [medTemplate],
                        },
                        false,
                    );

                    await pushGenieToBE(getMyDbId() || '', ['medTemplates']);
                    break;

                case 'injections':
                    const injectionTemplate: GInjectionTemplate = {
                        id: action.id || `${LOCAL_ID_PREFIX}${new Date().getTime()}`,
                        name: action.name,

                        [action.entity]: source[action.entity] || [],
                    };

                    await updateGenie(
                        getMyDbId() || '',
                        {
                            key: DB_TABLES['gInjectionTemplates'],
                            value: [injectionTemplate],
                        },
                        false,
                    );

                    await pushGenieToBE(getMyDbId() || '', ['injectionTemplates']);
                    break;

                case 'symptoms':
                    const symTemplate: GSymTemplate = {
                        id: action.id || `${LOCAL_ID_PREFIX}${new Date().getTime()}`,
                        name: action.name,

                        [action.entity]:
                            source[action.entity]?.map((s) => ({ id: s.id, name: s.name })) || [],
                    };

                    await updateGenie(
                        getMyDbId() || '',
                        {
                            key: DB_TABLES['gSymTemplates'],
                            value: [symTemplate],
                        },
                        false,
                    );

                    await pushGenieToBE(getMyDbId() || '', ['symTemplates']);
                    break;

                case 'diagnosis':
                    const diaTemplate: GDiaTemplate = {
                        id: action.id || `${LOCAL_ID_PREFIX}${new Date().getTime()}`,
                        name: action.name,

                        [action.entity]:
                            source[action.entity]?.map((s) => ({ id: s.id, name: s.name })) || [],
                    };

                    await updateGenie(
                        getMyDbId() || '',
                        {
                            key: DB_TABLES['gDiaTemplates'],
                            value: [diaTemplate],
                        },
                        false,
                    );

                    await pushGenieToBE(getMyDbId() || '', ['diaTemplates']);
                    break;
                case 'labVitals':
                    const labVitalsTemplate: GLabVitalTemplate = {
                        id: action.id || `${LOCAL_ID_PREFIX}${new Date().getTime()}`,
                        name: action.name,

                        [action.entity]:
                            source[action.entity]?.map((s) => ({
                                id: s.id,
                                name: s.name,
                                unit_dislay_name: s.unit_dislay_name,
                                all_units: s.all_units,
                                unit: s.unit,
                                is_panel: s.is_panel,
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                meta: s.meta,
                            })) || [],
                    };

                    await updateGenie(
                        getMyDbId() || '',
                        {
                            key: DB_TABLES['gLabVitalsTemplates'],
                            value: [labVitalsTemplate],
                        },
                        false,
                    );

                    await pushGenieToBE(getMyDbId() || '', ['labVitalsTemplates']);
                    break;
                case 'examinations':
                    const examinationsTemplates: GExaminationsTemplate = {
                        id: action.id || `${LOCAL_ID_PREFIX}${new Date().getTime()}`,
                        name: action.name,

                        [action.entity]:
                            source[action.entity]?.map((s) => ({
                                id: s.id,
                                name: s.name,
                                notes: s.notes,
                            })) || [],
                    };

                    await updateGenie(
                        getMyDbId() || '',
                        {
                            key: DB_TABLES['gExaminationsTemplates'],
                            value: [examinationsTemplates],
                        },
                        false,
                    );

                    await pushGenieToBE(getMyDbId() || '', ['examinationsTemplates']);
                    break;
                case 'procedures':
                    const proceduresTemplates: GProceduresTemplate = {
                        id: action.id || `${LOCAL_ID_PREFIX}${new Date().getTime()}`,
                        name: action.name,

                        [action.entity]:
                            source[action.entity]?.map((s) => ({
                                id: s.id,
                                name: s.name,
                                notes: s.notes,
                            })) || [],
                    };

                    await updateGenie(
                        getMyDbId() || '',
                        {
                            key: DB_TABLES['gProceduresTemplates'],
                            value: [proceduresTemplates],
                        },
                        false,
                    );

                    await pushGenieToBE(getMyDbId() || '', ['proceduresTemplates']);
                    break;
            }

            break;

        case DELETE_ADVICE_PERMANENTLY:
            await deleteG(getMyDbId() || '', DB_TABLES['advices'], action.text);
            await pushGenieToBE(getMyDbId() || '', ['advices']);
            break;

        case EDIT_ADVICE:
            if (action.oldAdvice) {
                try {
                    const adviceInDb = await getGenieItemById(
                        getMyDbId() || '',
                        DB_TABLES.advices,
                        action?.oldAdvice.text,
                    );

                    if (!adviceInDb) {
                        return;
                    }

                    await deleteG(getMyDbId() || '', DB_TABLES['advices'], action.oldAdvice.text);

                    const advice: GAdvice = {
                        id: action.advice.id,
                        text: action.advice.text,
                        parsedText: action.advice.parsedText,
                        meta: {
                            updatedAt: new Date().getTime(),
                            isLocal: 1,
                            fCount:
                                adviceInDb?.meta?.fCount && !isNaN(adviceInDb?.meta?.fCount)
                                    ? adviceInDb?.meta?.fCount
                                    : 0,
                        },
                    };

                    await updateGenie(
                        getMyDbId() || '',
                        {
                            key: DB_TABLES['advices'],
                            value: [advice],
                        },
                        false,
                    );
                    await pushGenieToBE(getMyDbId() || '', ['advices']);
                } catch (e) {
                    console.error('Error upserting advices genie data', e);
                }
            }
            break;

        case DELETE_PRESCRIPTION_TEMPLATE:
            switch (action.entity) {
                case 'PX':
                    await deleteG(getMyDbId() || '', DB_TABLES['prescriptionTemplates'], action.id);
                    await pushGenieToBE(getMyDbId() || '', ['templates']);
                    break;

                case 'labTests':
                    await deleteG(getMyDbId() || '', DB_TABLES['gLTTemplates'], action.id);
                    await pushGenieToBE(getMyDbId() || '', ['ltTemplates']);
                    break;

                case 'medications':
                    await deleteG(getMyDbId() || '', DB_TABLES['gMedTemplates'], action.id);
                    await pushGenieToBE(getMyDbId() || '', ['medTemplates']);
                    break;

                case 'injections':
                    await deleteG(getMyDbId() || '', DB_TABLES['gInjectionTemplates'], action.id);
                    await pushGenieToBE(getMyDbId() || '', ['injectionTemplates']);
                    break;

                case 'symptoms':
                    await deleteG(getMyDbId() || '', DB_TABLES['gSymTemplates'], action.id);
                    await pushGenieToBE(getMyDbId() || '', ['symTemplates']);
                    break;

                case 'diagnosis':
                    await deleteG(getMyDbId() || '', DB_TABLES['gDiaTemplates'], action.id);
                    await pushGenieToBE(getMyDbId() || '', ['diaTemplates']);
                    break;
                case 'labVitals':
                    await deleteG(getMyDbId() || '', DB_TABLES['gLabVitalsTemplates'], action.id);
                    await pushGenieToBE(getMyDbId() || '', ['labVitalsTemplates']);
                    break;
                case 'examinations':
                    await deleteG(
                        getMyDbId() || '',
                        DB_TABLES['gExaminationsTemplates'],
                        action.id,
                    );
                    await pushGenieToBE(getMyDbId() || '', ['examinationsTemplates']);
                    break;
            }

            break;

        case UPDATE_VITALS_V0:
        case UPDATE_VITALS:
            if (!state.prescriptionPad.prescription) return;

            const { vitals } = state.prescriptionPad.prescription;

            const timestamp = new Date().getTime();
            const localVitals: GVitals[] | undefined = vitals
                ?.filter((v) => v.id.startsWith(LOCAL_ID_PREFIX))
                .map((v) => ({
                    id: v.id,
                    name: v.name,
                    unit: v.value.unit,
                    meta: {
                        isLocal: 1,
                        updatedAt: timestamp,
                        fCount: 0, //NA
                    },
                }));

            if (localVitals && localVitals?.length > 0) {
                await updateG(getMyDbId() || '', DB_TABLES['vitals'], localVitals);
                pushGenieToBE(getMyDbId() || '', ['vitals']);
            }

            break;

        case DELETE_VITALS:
            if (!state.prescriptionPad.prescription) return;

            if (action.isDelFromGenie && action?.id?.startsWith(LOCAL_ID_PREFIX)) {
                await deleteG(getMyDbId() || '', DB_TABLES['vitals'], action.id);
            }

            pushGenieToBE(getMyDbId() || '', ['vitals']);
            break;

        case UPSERT_PRESCRIPTION:
        case UPSERT_PRESCRIPTION_V2:
            if (!state.prescriptionPad.prescription) return;

            // TODO
            // pushGenieToBE();
            break;

        case UPDATE_STAR_VITALS:
            const { data } = action;
            await updateItemInMisc(getMyDbId() || '', MISC_KEYS.keyVitals, data);

            try {
                await pushGenieToBE(getMyDbId() || '', ['keyVitals']);
            } catch (error) {
                captureError(error as Error);
                track('ERROR', {
                    on: 'mediaViewer',
                    error: (error as Error).message,
                });
            }

            break;

        case FINISH_PRESCRIPTION:
            const prescription = action.px || state.prescriptionPad.prescription;
            if (!prescription) return;

            // update genie store
            const {
                symptoms,
                diagnosis,
                medications,
                advices,
                labTests,
                examinations,
                refer,
                followup,
                dyform,
                labVitals,
                dentalProcedures,
                injections,
                procedures,
            } = prescription;
            const dentalExaminations = prescription.dentalExaminations?.filter(
                (i) => i.type !== DENTAL_ENTITY_TYPE.PROCEDURE,
            );
            const dentalPastProcedures = prescription.dentalExaminations?.filter(
                (i) => i.type === DENTAL_ENTITY_TYPE.PROCEDURE,
            );

            let gFollowUp: GFollowUpData | undefined;

            const gRefer: GReferData[] | undefined = refer?.doc?.map((t) => ({
                id: generateElementId(
                    ([t.name?.f, t.name?.l].filter(Boolean).join(' ').trim() || t.s_name || '') +
                        t.phone?.value +
                        t.id?.toString(),
                ),
                doc: t,
            }));

            if (followup && followup.date) {
                const diff = moment(new Date(followup.date).toDateString()).diff(
                    new Date().toDateString(),
                    'day',
                );
                gFollowUp = {
                    id: `${diff}-day`,
                    label: `${diff} ${getUnit('day', diff.toString())}`,
                    daysToAdd: diff,
                };
            }

            const aggr = {
                frequency: [] as GMedFrequency[],
                soa: [] as GMedSOA[],
                timing: [] as GMedTiming[],
            };

            if (medications) {
                const allMed = medications.reduce((all, med) => {
                    all.push(med);
                    med.tapering_dose && all.push(...med.tapering_dose);
                    return all;
                }, [] as MedDoseInfo[]);

                for (let index = 0; index < allMed.length; index++) {
                    const med = allMed[index];
                    if (med.frequency?.custom) {
                        if (
                            !PROPERTIES.med.Frequency.find(
                                (t) => t.n.toLowerCase() === med.frequency?.custom?.toLowerCase(),
                            )
                        ) {
                            aggr.frequency.push({
                                id: generateElementId(med.frequency.custom.toLowerCase()),
                                n: med.frequency.custom,
                            });
                        }
                    }

                    if (med.timing) {
                        if (
                            !PROPERTIES.med['Dose Timing'].find(
                                (t) => t.n.toLowerCase() === med.timing?.toLowerCase(),
                            )
                        ) {
                            aggr.timing.push({
                                id: generateElementId(med.timing.toLowerCase()),
                                n: med.timing,
                            });
                        }
                    }

                    if (med.area) {
                        if (!SITE_OF_APPLICATION.find((t) => t.id === med.area?.id)) {
                            aggr.soa.push(med.area);
                        }
                    }
                }
            }

            await updateGenieProfile(
                getMyDbId() || '',
                {
                    symptoms,
                    diagnosis,
                    medications,
                    advices,
                    labTests,
                    examinations,
                    refer: gRefer ? gRefer : [],
                    followUp: gFollowUp ? [gFollowUp] : [],
                    padDForms: dyform,
                    medFrequency: aggr.frequency,
                    medTiming: aggr.timing,
                    medSOA: aggr.soa,
                    labVitals:
                        // TODO make track object streamline in the types
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        labVitals?.filter((t) => t.track?.source !== SOURCE_MEDICAL_RECORDS) || [],
                    dentalExaminations,
                    dentalProcedures,
                    dentalPastProcedures,
                    injections,
                    procedures,
                },
                true,
            );

            // await pushGenieToBE();

            break;
        default:
    }
};

const PURGE_MAP: {
    [key in DB_TABLES]: {
        maxCount: number;
    };
} = {
    [DB_TABLES.symptoms]: {
        maxCount: MAX_G_SYMPTOMS,
    },
    [DB_TABLES.diagnosis]: {
        maxCount: MAX_G_DIAGNOSIS,
    },
    [DB_TABLES.medications]: {
        maxCount: MAX_G_MEDICATIONS,
    },
    [DB_TABLES.injections]: {
        maxCount: MAX_G_MEDICATIONS,
    },
    [DB_TABLES.advices]: {
        maxCount: MAX_G_ADVICES,
    },
    [DB_TABLES.prescriptionTemplates]: {
        maxCount: MAX_G_PRESCRIPTION_TEMPLATES,
    },
    [DB_TABLES.vitals]: {
        maxCount: MAX_G_VITALS,
    },
    [DB_TABLES.labTests]: {
        maxCount: MAX_G_LAB_TESTS,
    },
    [DB_TABLES.examinations]: {
        maxCount: MAX_G_EXAMINATIONS,
    },
    [DB_TABLES.refer]: {
        maxCount: MAX_G_REFER,
    },
    [DB_TABLES.followUp]: {
        maxCount: MAX_G_FOLLOWUP,
    },
    [DB_TABLES.services]: {
        maxCount: MAX_G_SERVICES,
    },
    [DB_TABLES.dyForm]: {
        maxCount: MAX_G_DYFORM,
    },
    [DB_TABLES.medFrequency]: {
        maxCount: MAX_GENIE,
    },
    [DB_TABLES.medTiming]: {
        maxCount: MAX_GENIE,
    },
    [DB_TABLES.medSOA]: {
        maxCount: MAX_GENIE,
    },
    [DB_TABLES.labVitals]: {
        maxCount: MAX_G_LAB_VITALS,
    },
    gLTTemplates: {
        maxCount: MAX_G_LT_TEMPLATES,
    },
    gMedTemplates: {
        maxCount: MAX_G_MED_TEMPLATES,
    },
    gInjectionTemplates: {
        maxCount: MAX_GENIE,
    },
    gSymTemplates: {
        maxCount: MAX_G_SYM_TEMPLATES,
    },
    gDiaTemplates: {
        maxCount: MAX_G_DIA_TEMPLATES,
    },
    gLabVitalsTemplates: {
        maxCount: MAX_G_LAB_VITALS_TEMPLATES,
    },
    gExaminationsTemplates: {
        maxCount: MAX_G_EXAMINATIONS_TEMPLATES,
    },
    gProceduresTemplates: {
        maxCount: MAX_G_PROCEDURES_TEMPLATES,
    },
    inventory: {
        maxCount: MAX_INVENTORY,
    },
    snackingQuestions: {
        maxCount: MAX_SNACKING_QUESTION,
    },
    inventoryV2: {
        maxCount: MAX_INVENTORY,
    },
    [DB_TABLES.dentalExaminations]: {
        maxCount: MAX_G_DENTAL_EXAMINATIONS,
    },
    [DB_TABLES.dentalProcedures]: {
        maxCount: MAX_G_DENTAL_PROCEDURES,
    },
    [DB_TABLES.dentalPastProcedures]: {
        maxCount: MAX_G_DENTAL_PAST_PROCEDURES,
    },
    [DB_TABLES.procedures]: {
        maxCount: MAX_G_PROCEDURES,
    },
    [DB_TABLES.medicalRecordsTags]: {
        maxCount: MAX_G_MEDICAL_RECORDS_TAGS,
    },
    [DB_TABLES.DB_TABLE_MISC]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.visits]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.patients]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.activeOrderUpdates]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.prescriptions]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.DB_TABLE_D_FORMS]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.milestones]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.treatmentServices]: {
        maxCount: MAX_G_TREATMENT_SERVICES,
    },
    [DB_TABLES.treatmentPackages]: {
        maxCount: MAX_G_TREATMENT_PACKAGES,
    },
    [DB_TABLES.doctors]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.todayAppointments]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.roles]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.doctorsInBiz]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
    [DB_TABLES.appointments]: {
        maxCount: Number.MAX_SAFE_INTEGER,
    },
};

const purge = async (entity: DB_TABLES) => {
    const config = PURGE_MAP[entity];

    const count = await getGCount(getMyDbId() || '', entity);

    if (count > config.maxCount) {
        await purgeG(getMyDbId() || '', entity, config.maxCount);
    }
};

const createGenieReduxMiddleware = () => (api: MiddlewareAPI<Dispatch, GlobalState>) => {
    return (next: Dispatch<Actions>) => (action: Action) => {
        const result = next(action);
        handleAction(action, api.getState(), api.dispatch).catch((err) => {
            console.log('genieReduxMiddleware: Error handelling action', err);
            captureErrorString('genieReduxMiddleware: Error handelling action ' + err.message);
        });
        return result;
    };
};

const genieReduxMiddleware = createGenieReduxMiddleware();

export default genieReduxMiddleware;

const purgeDyFormElements = (elements: PadFDFormElement[]) => {
    return elements
        .sort((a, b) => {
            const aTime = a.meta?.updatedAt || 0;
            const bTime = b.meta?.updatedAt || 0;
            return bTime - aTime;
        })
        .splice(0, MAX_G_DYFORM_ELEMENTS);
};

export function getTemplatizedFollowUp(
    followup?: Followup,
): { msFromNow?: number | undefined; optionId?: string | undefined } | undefined {
    return followup && followup.date
        ? {
              msFromNow:
                  new Date(new Date(followup.date).toDateString()).getTime() -
                  new Date(new Date().toDateString()).getTime(),
              optionId: followup.optionId,
          }
        : undefined;
}

async function handleUpsertDyForm(
    action: UpsertDyFormAction,
    state: GlobalState,
    dispatch: ThunkDispatch<GlobalState, null, Actions>,
) {
    const padElements = getPxElementConfig(state);
    // if it's a new one, add to the pad elements
    const found = padElements.find((e) => e.id === action.dyform.id);
    const geniePadDForm = await getGById<GDyForm>(
        getMyDbId() || '',
        DB_TABLES['dyForm'],
        action.dyform.id,
    );

    // add only if not present in the genie
    if (!geniePadDForm) {
        await updateG<GDyForm>(getMyDbId() || '', DB_TABLES['dyForm'], [action.dyform]);
    }

    // if it's a new one or the name got chanced, add to the pad element
    if (!found || found.name !== action.dyform.name) {
        dispatch(
            upsertGenieSetting({
                padElements: cloneAndUpsert(
                    {
                        id: action.dyform.id,
                        name: action.dyform.name,
                        isShown: true,
                        isAutoCopy: false,
                    },
                    padElements,
                ),
            }),
        );
    }
    const padDyForm = state.session.padConfig?.dyForm;
    if (!padDyForm?.find((e) => e.id === action.dyform.id)) {
        dispatch(loadPadDyForm(cloneAndUpsert(action.dyform, padDyForm)));
    }
    const printConfig = getPrintConfigs(state);
    if (!printConfig?.dyform) {
        track('ERROR', { on: 'UPSERT_DYFORM', what: 'print config not found' });
        return;
    }
    // if it's a new one, add to the print config
    if (printConfig?.dyform[action.dyform.id] === undefined) {
        dispatch(
            updatePrintConfig({
                ...printConfig,
                dyform: { ...printConfig.dyform, [action.dyform.id]: true },
            }),
        );
    }
    PRINT_CONFIGS_KEYS_TO_LABEL[action.dyform.id] = action.dyform.name;
}

async function handleDeleteDyForm(
    action: DeleteDyFormAction,
    state: GlobalState,
    dispatch: ThunkDispatch<GlobalState, null, Actions>,
) {
    const padElements = getPxElementConfig(state);
    await deleteG(getMyDbId() || '', DB_TABLES['dyForm'], action.id);
    if (padElements.find((e) => e.id === action.id)) {
        dispatch(
            upsertGenieSetting({
                padElements: padElements.filter((e) => e.id !== action.id),
            }),
        );
    }
    const printConfig = getPrintConfigs(state);
    if (!printConfig?.dyform) {
        track('ERROR', { on: 'DELETE_DYFORM', what: 'print config not found' });
        return;
    }
    if (printConfig?.dyform[action.id] !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [action.id]: _unused, ...rest } = printConfig.dyform;
        dispatch(
            updatePrintConfig({
                ...printConfig,
                dyform: rest,
            }),
        );
    }
}
